import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Chip,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

const UpdateDialog = ({
  open,
  onClose,
  onConfirm,
  customers,
  customersLoading,
  initialCustSernum,
  onLoadSites,
}) => {
  const [custId, setCustId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerFocused, setCustomerFocused] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [siteSearch, setSiteSearch] = useState("");
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const customerInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCustId(initialCustSernum || "");
      setCustomerSearch("");
      setCustomerFocused(false);
      setSelectedCustomer(null);
      setSiteSearch("");
      setSelectedSite(null);
      setSites([]);
    }
  }, [open, initialCustSernum]);

  const showCustomerList =
    (customerFocused || customerSearch.length > 0) && !selectedCustomer;

  const filteredCustomers = useMemo(() => {
    const list = customers || [];
    if (!customerSearch.trim()) return list.slice(0, 60);
    const q = customerSearch.toLowerCase();
    return list
      .filter(
        (c) =>
          c.ACCDES?.toLowerCase().includes(q) ||
          c.EACCDES?.toLowerCase().includes(q) ||
          c.ACCNAME?.toLowerCase().includes(q),
      )
      .slice(0, 60);
  }, [customers, customerSearch]);

  const filteredSites = useMemo(() => {
    if (!siteSearch.trim()) return sites;
    const q = siteSearch.toLowerCase();
    return sites.filter(
      (s) =>
        s.CODEDES?.toLowerCase().includes(q) ||
        s.CODE?.toLowerCase().includes(q),
    );
  }, [sites, siteSearch]);

  const handleCustomerPick = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch("");
    setCustomerFocused(false);
    setSelectedSite(null);
    setSiteSearch("");
    setSitesLoading(true);
    try {
      const loaded = await onLoadSites(customer);
      setSites(loaded || []);
    } catch {
      setSites([]);
    } finally {
      setSitesLoading(false);
    }
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch("");
    setSelectedSite(null);
    setSiteSearch("");
    setSites([]);
    setTimeout(() => customerInputRef.current?.focus(), 50);
  };

  // Disable confirm only when customer selected but site not yet chosen
  const confirmDisabled = selectedCustomer && !selectedSite;

  const handleConfirm = () => {
    onConfirm({
      custId: custId.trim(),
      customer: selectedCustomer,
      site: selectedSite,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 3, direction: "ltr", maxHeight: "92dvh" },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem", pb: 1 }}>
        עדכון
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "16px !important" }}
      >
        {/* זיהוי לקוח */}
        <TextField
          label="זיהוי לקוח"
          value={custId}
          onChange={(e) => setCustId(e.target.value)}
          fullWidth
          size="small"
          autoFocus
        />

        {/* Customer section */}
        <Box>
          <Typography
            sx={{ fontSize: "0.8rem", color: "#666", mb: 0.75, textAlign: "left" }}
          >
            לקוח
          </Typography>

          {selectedCustomer ? (
            <Chip
              label={selectedCustomer.ACCDES || selectedCustomer.ACCNAME}
              onDelete={clearCustomer}
              deleteIcon={<CloseIcon />}
              sx={{
                bgcolor: "#e8f0fe",
                color: "#0563B0",
                fontWeight: 600,
                fontSize: "0.85rem",
                height: 34,
                width: "100%",
                justifyContent: "space-between",
                "& .MuiChip-label": { flex: 1 },
              }}
            />
          ) : (
            <TextField
              inputRef={customerInputRef}
              placeholder="חפש לפי שם לקוח..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              onFocus={() => setCustomerFocused(true)}
              onBlur={() => setTimeout(() => setCustomerFocused(false), 150)}
              fullWidth
              size="small"
              disabled={customersLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {customersLoading ? (
                      <CircularProgress size={14} />
                    ) : (
                      <SearchIcon sx={{ fontSize: "1.1rem", color: "#999" }} />
                    )}
                  </InputAdornment>
                ),
                endAdornment: customerSearch ? (
                  <InputAdornment position="end">
                    <CloseIcon
                      sx={{ fontSize: "1rem", cursor: "pointer", color: "#999" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setCustomerSearch("");
                      }}
                    />
                  </InputAdornment>
                ) : null,
              }}
            />
          )}

          {showCustomerList && (
            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                mt: 0.5,
              }}
            >
              {filteredCustomers.length === 0 ? (
                <Typography
                  sx={{ p: 1.5, fontSize: "0.82rem", color: "#999", textAlign: "center" }}
                >
                  לא נמצאו תוצאות
                </Typography>
              ) : (
                <List dense disablePadding>
                  {filteredCustomers.map((c) => (
                    <ListItem key={c.ACCNAME} disablePadding divider>
                      <ListItemButton
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleCustomerPick(c)}
                        sx={{ py: 0.75 }}
                      >
                        <ListItemText
                          primary={c.ACCDES || c.EACCDES}
                          primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </Box>

        {/* Site section — shown after customer selected */}
        {selectedCustomer && (
          <Box>
            <Typography
              sx={{ fontSize: "0.8rem", color: "#666", mb: 0.75, textAlign: "left" }}
            >
              אתר
            </Typography>

            {selectedSite ? (
              <Chip
                label={selectedSite.CODEDES}
                onDelete={() => { setSelectedSite(null); setSiteSearch(""); }}
                deleteIcon={<CloseIcon />}
                sx={{
                  bgcolor: "#e8f0fe",
                  color: "#0563B0",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  height: 34,
                  width: "100%",
                  justifyContent: "space-between",
                  "& .MuiChip-label": { flex: 1 },
                }}
              />
            ) : (
              <>
                <TextField
                  placeholder="חפש אתר..."
                  value={siteSearch}
                  onChange={(e) => setSiteSearch(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: "1.1rem", color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box
                  sx={{
                    maxHeight: 180,
                    overflowY: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    mt: 0.5,
                  }}
                >
                  {sitesLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : filteredSites.length === 0 ? (
                    <Typography
                      sx={{ p: 1.5, fontSize: "0.82rem", color: "#999", textAlign: "center" }}
                    >
                      אין אתרים
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {filteredSites.map((s) => (
                        <ListItem key={s.CODE} disablePadding divider>
                          <ListItemButton
                            onClick={() => setSelectedSite(s)}
                            sx={{ py: 0.75 }}
                          >
                            <ListItemText
                              primary={s.CODEDES}
                              secondary={s.CODE}
                              primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
                              secondaryTypographyProps={{ fontSize: "0.75rem" }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
                <Typography sx={{ fontSize: "0.78rem", color: "#e65100", mt: 0.5, textAlign: "right" }}>
                  נא לבחור אתר
                </Typography>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2, fontWeight: 600, flex: 1 }}
        >
          ביטול
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={confirmDisabled}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            flex: 1,
            bgcolor: "#0563B0",
            "&:hover": { bgcolor: "#044a8a" },
          }}
        >
          אישור
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateDialog;
